import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Form,
    Typography,
    Row,
    Col,
    Button,
} from 'antd';
import DashedContainer from '@/components/DashedContainer';
import TourExtraServices from '@/components/TourExtraServices';
import useApiValidationsForForm from '@/hooks/useApiValidationsForForm';
import { extractUrlFromFile } from '@/helpers/upload';
import { returnDeletedImages } from '@/helpers/deleteImages';
import { actions } from '@/redux/accommodation/hotel/reducer';
import { selectors } from '@/redux/accommodation/hotel/create/model';
import { useCurrency } from '@/hooks/useCurrency';
import { formatExtraServicesForSubmit, eServicesFormat } from '@/helpers/mapping';
import useTranslatedValidationConfig from '@/hooks/useTranslatedValidationConfig';

import RoomDetails from './RoomDetails';
import PersonAndBed from './PersonAndBed';
import ExtraBed from './ExtraBed';
import Amenities from './Amenities';
import ImagesUpload from './ImagesUpload';
import RoomPolicy from '../../RoomPolicy';
import baseValidations from './validations';

const formFields = [
    'type_name',
    'room_size',
    'no_rooms',
    'price',
    'commission',
    'commission_type',
    'adults',
    'adult_bed_no',
    'bed_name_adult',
    'children',
    'children_bed_no',
    'bed_name_children',
    'extra_beds',
    'extra_bed_charge',
    'extra_bed_charges_type',
    'amenities',
];

const AddNewRoom = ({ hotelId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const { icon: currencyIcon } = useCurrency();

    const validations = useTranslatedValidationConfig(baseValidations);

    const [isRefundable, setIsRefundable] = useState(false);

    const goToRoomList = useCallback(() => {
        dispatch(actions.goToViewRoomList());
    }, [dispatch]);

    const room = useSelector(selectors.roomDetails);

    const initialImages = room?.images;

    const onFinish = useCallback((values) => {
        const data = {
            type_name: values.type_name,
            room_size: values.room_size,
            no_rooms: values.no_rooms,

            price_bb: values.price_bb,
            commission_bb: values.commission_bb,
            commission_type_bb: values.commission_type_bb,

            price_hb: values.price_hb,
            commission_hb: values.commission_hb,
            commission_type_hb: values.commission_type_hb,

            price_fb: values.price_fb,
            commission_fb: values.commission_fb,
            commission_type_fb: values.commission_type_fb,

            price_ai: values.price_ai,
            commission_ai: values.commission_ai,
            commission_type_ai: values.commission_type_ai,

            adults: values.adults,
            adult_bed_no: values.adult_bed_no,
            bed_name_adult: values.bed_name_adult,

            children: values.children,
            children_bed_no: values.children_bed_no,
            bed_name_children: values.bed_name_children,

            extra_beds: values.extra_beds,
            extra_bed_charge: values.extra_bed_charge,
            extra_bed_charges_type: values.extra_bed_charges_type,

            prices: values.prices
                ? values.prices.map((price, index) => ({
                    [`child_${index + 1}`]: price.child_policy,
                }))
                : [],

            extra_adult_prices: values.extra_adult_prices,
            minimum_stay_required: values.minimum_stay_required,
            prepayment_amount: values.prepayment_amount,
            prepayment_amount_type: values.prepayment_amount_type,
            is_refundable: values.is_refundable,
            refundable_amount: values.refundable_amount,
            refundable_amount_type: values.refundable_amount_type,
            total_payment_required_before_check_in: values.total_payment_required_before_check_in,
            refundable_cancellation: values.refundable_cancellation,

            amenities: values.amenities,
            images: values.images
                ? values.images.fileList?.map(extractUrlFromFile)
                : [],
            deleted_images: returnDeletedImages(initialImages, values.images?.fileList),
            extra_services: values.extra_services
                ? formatExtraServicesForSubmit(values.extra_services)
                : [],
        };
        dispatch(actions.requestSubmitAccommodationRoom(data));
    }, [dispatch]);

    const errors = useSelector(selectors.errors);

    useApiValidationsForForm({ form, errors, formFields });

    const initialData = useMemo(() => {
        if (room) {
            return {
                type_name: room.type_name,
                room_size: room.room_size,
                no_rooms: room.no_rooms,

                price_bb: room.price_bb,
                commission_bb: room.commission_bb,
                commission_type_bb: room.commission_type_bb,

                price_hb: room.price_hb,
                commission_hb: room.commission_hb,
                commission_type_hb: room.commission_type_hb,

                price_fb: room.price_fb,
                commission_fb: room.commission_fb,
                commission_type_fb: room.commission_type_fb,

                price_ai: room.price_ai,
                commission_ai: room.commission_ai,
                commission_type_ai: room.commission_type_ai,

                adults: room.adults,
                adult_bed_no: room.adult_bed_no,
                bed_name_adult: room.bed_name_adult,

                children: room.children,
                children_bed_no: room.children_bed_no,
                bed_name_children: room.bed_name_children,

                extra_beds: room.extra_beds,
                extra_bed_charge: room.extra_bed_charge,
                extra_bed_charges_type: room.extra_bed_charges_type,

                prices: room.prices
                    ? room.prices.map((price, index) => ({
                        child_policy: price[`child_${index + 1}`],
                    }))
                    : [],

                extra_adult_prices: room.extra_adult_prices,
                minimum_stay_required: room.minimum_stay_required,
                prepayment_amount: room.prepayment_amount,
                prepayment_amount_type: room.prepayment_amount_type,
                is_refundable: room.is_refundable,
                refundable_amount: room.refundable_amount,
                refundable_amount_type: room.refundable_amount_type,
                total_payment_required_before_check_in: room.total_payment_required_before_check_in,
                refundable_cancellation: room.refundable_cancellation,

                amenities: room.amenities.selected
                    ? room.amenities.selected.map((amenity) => (amenity.id))
                    : [],
                images: room.images.map((image) => (
                    {
                        id: image.id,
                        url: image.path,
                        thumbUrl: image.path,
                    }
                )),
                extra_services: eServicesFormat(room.services),
            };
        } else {
            return {
                commission_type_bb: 4,
                commission_type_hb: 4,
                commission_type_fb: 4,
                commission_type_ai: 4,
                extra_bed_charges_type: 4,
                prepayment_amount_type: 4,
                refundable_amount_type: 4,
            };
        }
    }, [room]);

    useEffect(() => {
        if (room) {
            setIsRefundable(room.is_refundable);
        }
    }, [room]);

    return (
        <Form
            id="hotel-rooms-form"
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onFinish}
            initialValues={initialData || {}}
        >
            <Form.Item>
                <RoomDetails currency={currencyIcon} data={room} />
            </Form.Item>

            <Form.Item>
                <PersonAndBed
                    currency={currencyIcon}
                    validations={validations}
                />
            </Form.Item>

            <Form.Item>
                <ExtraBed currency={currencyIcon} />
            </Form.Item>

            <DashedContainer title="Room Policy">
                <Form.Item>
                    <RoomPolicy
                        currency={currencyIcon}
                        validations={validations}
                        isRefundable={isRefundable}
                        setIsRefundable={setIsRefundable}
                    />
                </Form.Item>
            </DashedContainer>

            <DashedContainer title="Amenities">
                <Form.Item>
                    <Amenities
                        hotelId={hotelId}
                        initialAmenities={initialData?.amenities}
                    />
                </Form.Item>
            </DashedContainer>

            <DashedContainer title="Extra Services">
                <TourExtraServices
                    name="extra_services"
                    checkboxText="The price is inclusive to the tour"
                />
            </DashedContainer>

            <Form.Item>
                <Typography.Text className="images-upload_text">
                    {t('Room Photo Gallery')}
                </Typography.Text>
                <ImagesUpload />
            </Form.Item>

            <Form.Item>
                <Row justify="end">
                    <Col pull={1}>
                        <Button
                            danger
                            type="primary"
                            onClick={goToRoomList}
                        >
                            {t('Cancel')}
                        </Button>
                    </Col>
                    <Col>
                        <Button type="primary" htmlType="submit">
                            {t('Submit')}
                        </Button>
                    </Col>
                </Row>
            </Form.Item>
        </Form>
    );
};

export default AddNewRoom;
